/**
 * ActiveRunsTable — paginated table of active / recent runs.
 *
 * Columns: Status | Run ID | Agent | Trigger | Duration | Started
 *
 * Fetches from GET /v1/runs?status=running,queued,waiting,admitted
 * Falls back to inline mock data when the API is unreachable.
 *
 * Click a row to select the run (calls onSelectRun).
 */

import { useState, useEffect, useCallback } from 'react'
import { StatusBadge } from '../../components/common/StatusBadge'
import { Pagination }  from '../../components/common/Pagination'
import type { Run }    from '../../api/types'
import { api }         from '../../api/client'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ActiveRunsTableProps {
  tenantId:    string
  activeRunId?: string
  onSelectRun: (runId: string) => void
  /** Refresh interval in ms. Default 5000. */
  refreshMs?: number
  pageSize?:  number
}

// ── Mock fallback ─────────────────────────────────────────────────────────────

const MOCK_RUNS: Run[] = [
  {
    id:          'run-00000001-mock',
    agentId:     'agent-research-01',
    tenantId:    'dev-tenant',
    status:      'running',
    triggerType: 'manual',
    createdAt:   new Date(Date.now() - 62_000).toISOString(),
    startedAt:   new Date(Date.now() - 62_000).toISOString(),
    agentName:   'Market Researcher',
  },
  {
    id:          'run-00000002-mock',
    agentId:     'agent-finance-01',
    tenantId:    'dev-tenant',
    status:      'waiting',
    triggerType: 'schedule',
    createdAt:   new Date(Date.now() - 310_000).toISOString(),
    startedAt:   new Date(Date.now() - 310_000).toISOString(),
    agentName:   'Finance Analyst',
  },
  {
    id:          'run-00000003-mock',
    agentId:     'agent-legal-01',
    tenantId:    'dev-tenant',
    status:      'queued',
    triggerType: 'api',
    createdAt:   new Date(Date.now() -  8_000).toISOString(),
    agentName:   'Contract Reviewer',
  },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(startedAt: string | null | undefined, finishedAt?: string | null): string {
  if (!startedAt) return '—'
  const start = new Date(startedAt).getTime()
  const end   = finishedAt ? new Date(finishedAt).getTime() : Date.now()
  const secs  = Math.floor((end - start) / 1000)
  if (secs < 60)   return `${secs}s`
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`
}

function formatStarted(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function shortId(id: string): string {
  return id.length > 18 ? id.slice(0, 8) + '…' + id.slice(-4) : id
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ActiveRunsTable({
  tenantId,
  activeRunId,
  onSelectRun,
  refreshMs  = 5_000,
  pageSize   = 10,
}: ActiveRunsTableProps) {
  const [runs,    setRuns]    = useState<Run[]>([])
  const [total,   setTotal]   = useState(0)
  const [page,    setPage]    = useState(1)
  const [loading, setLoading] = useState(true)
  const [useMock, setUseMock] = useState(false)
  const [_now,    setNow]     = useState(Date.now())   // force re-render for durations

  const fetchRuns = useCallback(async () => {
    try {
      // Routes through the shared api client: it attaches the (always-fresh in
      // workos mode) bearer token and handles 401-refresh. X-Tenant-Id is set to
      // the active tenant (ignored by the backend in workos mode).
      const json = await api.get<{ runs?: Run[]; items?: Run[]; total?: number } | Run[]>(
        '/v1/runs',
        {
          params: { status: 'running,queued,waiting,admitted', page, pageSize },
          headers: { 'X-Tenant-Id': tenantId },
        },
      )
      // Handle both {runs:[…], total:N} and flat array
      if (Array.isArray(json)) {
        setRuns(json)
        setTotal(json.length)
      } else {
        setRuns(json.runs ?? json.items ?? [])
        setTotal(json.total ?? json.runs?.length ?? 0)
      }
      setUseMock(false)
    } catch {
      setRuns(MOCK_RUNS)
      setTotal(MOCK_RUNS.length)
      setUseMock(true)
    } finally {
      setLoading(false)
    }
  }, [tenantId, page, pageSize])

  useEffect(() => {
    setLoading(true)
    fetchRuns()
    const tid = setInterval(() => {
      fetchRuns()
      setNow(Date.now())
    }, refreshMs)
    return () => clearInterval(tid)
  }, [fetchRuns, refreshMs])

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading && runs.length === 0) {
    return (
      <div className="py-8 px-5 text-center text-[#8aa0c2] text-[13px]">
        Loading active runs…
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0 bg-[var(--bg-glass)] backdrop-blur-[var(--glass-blur)] [-webkit-backdrop-filter:var(--glass-blur)] border-[var(--glass-border)] rounded-[var(--radius-lg)] shadow-[var(--glass-shadow)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-[10px] px-5 pt-[14px] pb-3 border-b border-[var(--border)]">
        <span className="text-[13px] font-bold text-text-primary flex-1">Active Runs</span>
        {useMock && (
          <span
            className="text-[10px] font-bold px-[7px] py-0.5 rounded-full bg-[var(--warning-dim)] text-[var(--warning)] border border-[rgba(245,158,11,0.22)] tracking-[0.05em] uppercase"
            title="API unreachable — showing demo data"
          >
            demo
          </span>
        )}
        <span className="text-[11px] text-text-muted">
          {total} run{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px] text-[var(--text-secondary)]" aria-label="Active runs">
          <thead>
            <tr className="bg-[rgba(255,255,255,0.02)]">
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Status</th>
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Run ID</th>
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Agent</th>
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Trigger</th>
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Steps</th>
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Duration</th>
              <th className="px-[14px] py-[9px] text-left text-[10px] font-bold tracking-[0.1em] uppercase text-text-muted border-b border-[var(--border)] whitespace-nowrap">Started</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 px-5 text-text-muted text-[13px]">
                  No active runs
                </td>
              </tr>
            ) : (
              runs.map((run) => (
                <tr
                  key={run.id}
                  className={`cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-hover)]${run.id === activeRunId ? ' !bg-[var(--accent-dim)]' : ''}`}
                  onClick={() => onSelectRun(run.id)}
                  tabIndex={0}
                  aria-selected={run.id === activeRunId}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onSelectRun(run.id)
                  }}
                >
                  <td className="px-[14px] py-[10px] border-b border-[var(--border)] align-middle">
                    <StatusBadge status={run.status} />
                  </td>
                  <td className="px-[14px] py-[10px] border-b border-[var(--border)] align-middle font-mono text-[11px] text-[var(--text-secondary)]">
                    <span title={run.id}>{shortId(run.id)}</span>
                  </td>
                  <td className="px-[14px] py-[10px] border-b border-[var(--border)] align-middle font-medium text-text-primary whitespace-nowrap">
                    {run.agentName ?? shortId(run.agentId)}
                  </td>
                  <td className="px-[14px] py-[10px] border-b border-[var(--border)] align-middle text-[11px] capitalize text-text-muted">
                    {run.triggerType}
                  </td>
                  <td className="px-[14px] py-[10px] border-b border-[var(--border)] align-middle font-mono text-[11px] text-[var(--text-secondary)] whitespace-nowrap">
                    {formatDuration(run.startedAt, run.finishedAt)}
                  </td>
                  <td className="px-[14px] py-[10px] border-b border-[var(--border)] align-middle font-mono text-[11px] text-text-muted whitespace-nowrap">
                    {run.createdAt ? formatStarted(run.createdAt) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex justify-end px-4 py-[10px] border-t border-[var(--border)]">
          <Pagination
            page={page}
            total={total}
            pageSize={pageSize}
            onChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
